import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import Datetime from "react-datetime";

/**
 * FloatingDateTime
 * A drop-in wrapper around react-datetime whose calendar is rendered through a
 * React portal to document.body. This lets the calendar float freely and
 * overflow the popup, instead of being clipped by an ancestor with
 * `overflow: auto` / `transform` (which is the case for the student profile popup).
 *
 * Props mirror the react-datetime props already used in the component:
 *  - inputProps  : props spread onto the visible text input (value, onChange, onBlur, placeholder, style, maxLength...)
 *  - value       : current value (string "MM/DD/YYYY" or moment/Date)
 *  - onChange    : called with the moment object when a date is picked in the calendar
 *  - isValidDate : react-datetime validator
 *  - dateFormat / timeFormat / className : forwarded to react-datetime
 */
const CALENDAR_WIDTH = 290; // approx rendered width of .rdtPicker
const CALENDAR_HEIGHT = 320; // approx rendered height, used to decide up/down

const FloatingDateTime = ({
  inputProps = {},
  value,
  onChange,
  isValidDate,
  dateFormat = "MM/DD/YYYY",
  timeFormat = false,
  className = "",
  initialViewDate,
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, openUp: false });
  const anchorRef = useRef(null);
  const popRef = useRef(null);

  // Recompute the fixed-position coordinates from the input's viewport rect.
  const reposition = useCallback(() => {
    try {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      // Flip upward only when there isn't room below but there is room above.
      const openUp = spaceBelow < CALENDAR_HEIGHT && rect.top > CALENDAR_HEIGHT;
      // Keep the calendar inside the viewport horizontally.
      const left = Math.max(
        8,
        Math.min(rect.left, window.innerWidth - CALENDAR_WIDTH - 8),
      );
      setCoords({
        top: openUp ? rect.top - 6 : rect.bottom + 6,
        left,
        openUp,
      });
    } catch (err) {
      // Positioning is best-effort; never let it break the form.
      console.error("FloatingDateTime reposition failed:", err);
    }
  }, []);

  // Reposition on open, and keep it glued to the input while scrolling/resizing.
  useEffect(() => {
    if (!open) return;
    reposition();
    const handle = () => reposition();
    window.addEventListener("scroll", handle, true); // capture = catch inner scroll containers
    window.addEventListener("resize", handle);
    return () => {
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle);
    };
  }, [open, reposition]);

  // Close when clicking outside both the input and the portaled calendar.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const inAnchor =
        anchorRef.current && anchorRef.current.contains(e.target);
      const inPop = popRef.current && popRef.current.contains(e.target);
      if (!inAnchor && !inPop) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const handleCalendarChange = (val) => {
    if (typeof onChange === "function") onChange(val);
    // Close once a full, valid date is selected from the grid.
    if (val && typeof val === "object" && val.isValid && val.isValid()) {
      setOpen(false);
    }
  };

  // Separate the handlers we need to augment from the rest of the input props.
  const {
    onChange: userChange,
    onFocus: userFocus,
    style,
    ...restInput
  } = inputProps;

  return (
    <div
      className="floating-datetime-anchor"
      ref={anchorRef}
      style={{ position: "relative", width: "100%" }}
    >
      <input
        {...restInput}
        style={style}
        value={value ?? ""}
        autoComplete="off"
        onChange={(e) => {
          if (typeof userChange === "function") userChange(e);
        }}
        onFocus={(e) => {
          setOpen(true);
          reposition();
          if (typeof userFocus === "function") userFocus(e);
        }}
        onClick={() => {
          setOpen(true);
          reposition();
        }}
      />

      {open &&
        createPortal(
          <div
            ref={popRef}
            className="floating-datetime-portal"
            style={{
              position: "fixed",
              left: coords.left,
              // When flipping up, anchor by bottom so the calendar grows upward.
              top: coords.openUp ? undefined : coords.top,
              bottom: coords.openUp
                ? window.innerHeight - coords.top
                : undefined,
              width: CALENDAR_WIDTH,
              zIndex: 999999, // above the popup (popup z-index is 113)
            }}
          >
            <Datetime
              input={false} /* render calendar only, no input */
              value={value}
              timeFormat={timeFormat}
              dateFormat={dateFormat}
              isValidDate={isValidDate}
              initialViewDate={initialViewDate}
              onChange={handleCalendarChange}
              className={className}
            />
          </div>,
          document.body,
        )}
    </div>
  );
};

export default FloatingDateTime;
